import React, { useMemo } from 'react';
import { Zap } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { getDamageAnalytics } from '../../lib/analytics';
import { SectionHeader, Card, ChartTooltip, MetricTile, Insight, EmptyState, CHART_COLORS } from './shared';
import PatchAnnotations from './PatchAnnotations';

export default function DamageSection({ games, champions, version }) {
  const data = useMemo(() => getDamageAnalytics(games), [games]);

  if (!data) {
    return <EmptyState message="Pas de données damage" sub="Saisis Damage Perso et Damage Équipe dans tes games" />;
  }

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile label="Dmg/Min Moyen" value={data.overallAvgDpm.toLocaleString()} color="cyan" />
        <MetricTile label="Games avec Data" value={data.totalGamesWithDmg} subtext={`sur ${games.length}`} color="white" />
        <MetricTile label="Meilleur Dmg/Min" value={data.perChampion[0]?.avgDpm.toLocaleString() || 0} subtext={data.perChampion[0]?.champion || ''} color="fuchsia" />
        <MetricTile label="Benchmark" value="800+" subtext="Master/GM ADC" color="amber" />
      </div>

      {/* Insight */}
      {data.overallAvgDpm > 0 && (
        <Insight variant={data.overallAvgDpm >= 800 ? 'success' : 'info'}>
          <strong>⚡ Ton profil damage</strong> — Tu fais en moyenne <strong>{data.overallAvgDpm.toLocaleString()} dmg/min</strong>.
          {data.overallAvgDpm >= 900 && ' Excellent output, bon niveau Master+.'}
          {data.overallAvgDpm >= 800 && data.overallAvgDpm < 900 && ' Bon niveau, proche des standards Master+.'}
          {data.overallAvgDpm < 800 && ' En dessous du benchmark Master ADC (~800). Vérifie tes items, build, et damage phase.'}
        </Insight>
      )}

      {/* Progression dmg/min */}
      {data.trends.length >= 3 && (
        <Card>
          <SectionHeader title="Évolution Damage" subtitle="Dmg/min et Damage Share rolling 10 games" icon={Zap} />
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="gameNumber" stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} />
              <YAxis yAxisId="left" stroke={CHART_COLORS.cyan} style={{ fontSize: 11, fontFamily: 'monospace' }} />
              <YAxis yAxisId="right" orientation="right" stroke={CHART_COLORS.fuchsia} style={{ fontSize: 11, fontFamily: 'monospace' }} domain={[0, 50]} />
              <Tooltip content={<ChartTooltip />} />
              <Line yAxisId="left" type="monotone" dataKey="dmgPerMin" stroke={CHART_COLORS.cyan} strokeWidth={2} dot={false} name="Dmg/Min" />
              <Line yAxisId="right" type="monotone" dataKey="dmgShare" stroke={CHART_COLORS.fuchsia} strokeWidth={2} dot={false} name="Damage Share %" />
            </LineChart>
          </ResponsiveContainer>
          <PatchAnnotations games={games} />
        </Card>
      )}

      {/* Corrélation damage share <-> winrate */}
      {data.shareWinrateCorrel.length >= 2 && (
        <Card>
          <SectionHeader title="Corrélation Damage Share ↔ Winrate" subtitle="Est-ce que faire plus de damage = gagner plus ?" />
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.shareWinrateCorrel}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="bucket" stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} />
              <YAxis stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} domain={[0, 100]} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="winrate" name="Winrate %">
                {data.shareWinrateCorrel.map((entry, i) => (
                  <Cell key={i} fill={entry.winrate >= 54 ? CHART_COLORS.cyan : CHART_COLORS.rose} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="text-[10px] text-zinc-500 font-mono mt-2">
            Les games où tu représentes une plus grande part du damage sont-elles plus souvent gagnées ?
          </div>
        </Card>
      )}
    </div>
  );
}
